import { IdentityTypes } from "@/interfaces/enums";
import log2sensors, { SensorsEventIds } from "@/utils/log2sensors";
import { useTranslation } from "next-i18next";
import React, { useCallback, useEffect, useMemo } from "react";
import SurveyTitle from "../SurveyTitleV2";
import SurveyFooter from "../SurveyFooter";
import Image from "next/image";

import ContentContainer from "@/containers/common/ContentContainer";
import Divider from "@/components/divider";
import { getCDNWithWebp } from "@/utils/getFullWebCdnUrl";
import clsx from "clsx";

interface PreUploadIDCardProps {
	answerFieldToValueMap: Map<string, string>;
	identity?: number;
	onNextClick: () => void;
}

function PreUploadIDCard(props: PreUploadIDCardProps) {
	const { t } = useTranslation("survey");

	const isPassport = () => {
		const documentType = props.identity || Number(props.answerFieldToValueMap.get("documentType"));
		return documentType === IdentityTypes.PASSPORT;
	};

	const getRequireCOnfigs = useCallback(() => {
		const documentType = props.identity || Number(props.answerFieldToValueMap.get("documentType"));
		switch (documentType) {
			case IdentityTypes.PASSPORT:
				return [
					{
						title: t("your-photo-must"),
						type: "correct",
						list: [
							t("be-a-clear-color-image"),
							t("show-all-four-corners"),
							t("show-the-entire-page-including-your-face"),
						],
					},
					{
						title: t("we-can-not-accept"),
						type: "error",
						list: [t("expired-passport"), t("scans-copies-or-screenshots")],
					},
				];
			case IdentityTypes.ID_CARD:
				return [
					{
						title: t("your-photo-must"),
						type: "correct",
						list: [
							t("both-sides-of-the-card-need-to-be-uploaded"),
							t("be-a-clear-color-image"),
							t("show-all-four-corners"),
						],
					},
					{
						title: t("we-can-not-accept"),
						type: "error",
						list: [t("expired-id-card"), t("scans-copies-or-screenshots")],
					},
				];
			case IdentityTypes.ALIEN_ID_CARD:
				return [
					{
						title: t("your-photo-must"),
						type: "correct",
						list: [
							t("both-sides-of-the-card-need-to-be-uploaded"),
							t("be-a-clear-color-image"),
							t("show-all-four-corners"),
						],
					},
					{
						title: t("we-can-not-accept"),
						type: "error",
						list: [t("expired-alien-id-card"), t("scans-copies-or-screenshots")],
					},
				];
			default:
				return [];
		}
	}, [props.answerFieldToValueMap, props.identity]);

	const icons = useMemo(() => {
		const documentType = props.identity || Number(props.answerFieldToValueMap.get("documentType"));
		if (documentType === IdentityTypes.PASSPORT) {
			return {
				correct: ["0rem"],
				error: [1, 2, 3].map((i) => -(i * 1.5625 + i * 3.875) + "rem"),
				bgWidth: "20.1875rem",
				bgHeight: "5rem",
				chidWidth: "3.875rem",
			};
		}

		return {
			correct: ["0rem"],
			error: [1, 2, 3].map((i) => -(i * 1.5 + i * 3.92) + "rem"),
			bgWidth: "20.125rem",
			bgHeight: "2.8125rem",
			chidWidth: "3.875rem",
		};
	}, [props.answerFieldToValueMap, props.identity]);

	const renderDocumentTypeText = () => {
		const documentType = props.identity || Number(props.answerFieldToValueMap.get("documentType"));
		if (documentType === IdentityTypes.ID_CARD) {
			return t("id-card");
		} else if (documentType === IdentityTypes.PASSPORT) {
			return t("passport");
		} else if (documentType === IdentityTypes.ALIEN_ID_CARD) {
			return t("alien-id-card");
		}
		return "";
	};

	useEffect(() => {
		if (props.identity === IdentityTypes.PASSPORT) {
			log2sensors(SensorsEventIds.AO_UPLOADPASSPORT2_VIEW);
		} else if (props.identity === IdentityTypes.ID_CARD) {
			log2sensors(SensorsEventIds.AO_UPLOADIDCARD2_VIEW);
		} else {
			log2sensors(SensorsEventIds.AO_UPLOADALIEN2_VIEW);
		}
	}, []);

	return (
		<ContentContainer
			className="px-4.5"
			animation
			header={
				<SurveyTitle
					title={t("submitting-a-phone-of-your", {
						type: renderDocumentTypeText(),
					})}
					subtitle={t("please-upload-clear-complete-and-watermark-free-photos-of-your-official-documents")}
				/>
			}
			footer={
				<SurveyFooter
					showSecure={false}
					showOnfido={true}
					onNextBtnClick={() => {
						props.onNextClick();
						if (props.identity === IdentityTypes.PASSPORT) {
							log2sensors(SensorsEventIds.AO_UPLOADPASSPORT2_NEXTPAGE);
						} else if (props.identity === IdentityTypes.ID_CARD) {
							log2sensors(SensorsEventIds.AO_UPLOADIDCARD2_NEXTPAGE);
						} else {
							log2sensors(SensorsEventIds.AO_UPLOADALIEN2_NEXTPAGE);
						}
					}}
					onPrivacyClick={() => {
						if (props.identity === IdentityTypes.PASSPORT) {
							log2sensors(SensorsEventIds.AO_UPLOADPASSPORT2_PRAVICY);
						} else if (props.identity === IdentityTypes.ID_CARD) {
							log2sensors(SensorsEventIds.AO_UPLOADIDCARD2_PRAVICY);
						} else {
							log2sensors(SensorsEventIds.AO_UPLOADALIEN2_PRAVICY);
						}
					}}
					onOnfidoClick={() => {
						if (props.identity === IdentityTypes.PASSPORT) {
							log2sensors(SensorsEventIds.AO_UPLOADPASSPORT2_ONFFIDO);
						} else if (props.identity === IdentityTypes.ID_CARD) {
							log2sensors(SensorsEventIds.AO_UPLOADIDCARD2_ONFFIDO);
						} else {
							log2sensors(SensorsEventIds.AO_UPLOADALIEN2_ONFFIDO);
						}
					}}
				/>
			}
		>
			<div className="h-2" />

			{getRequireCOnfigs().map((item, key) => {
				return (
					<div key={key} className="pt-[1.6vh]">
						<div className="relative flex items-center">
							{icons[item.type as "correct" | "error"].map((icon, index) => {
								return (
									<div
										key={index}
										className={clsx(
											"mr-5 bg-no-repeat",
											isPassport()
												? "bg-[url(https://web-cdn.rockflow.tech/h5/survey/pre-upload-id-card-light.png)] dark:bg-[url(https://web-cdn.rockflow.tech/h5/survey/pre-upload-id-card-dark.png)]"
												: "bg-[url(https://web-cdn.rockflow.tech/h5/survey/pre-upload-passport-light.png)] dark:bg-[url(https://web-cdn.rockflow.tech/h5/survey/pre-upload-passport-dark.png)]",
										)}
										style={{
											backgroundSize: `${icons.bgWidth} ${icons.bgHeight}`,
											width: icons.chidWidth,
											height: icons.bgHeight,
											backgroundPositionY: "0px",
											backgroundPositionX: icon,
										}}
									></div>
								);
							})}
						</div>

						<div className="pt-[1.2vh]">
							{item.list.map((subItem, subKey) => {
								return (
									<div key={`${key}${subKey}`} className="flex items-center mb-1.5">
										<div className="mt-1 flex-shrink-0">
											<Image
												src={
													item.type === "correct"
														? getCDNWithWebp("/survey/correct-icon.png")
														: getCDNWithWebp("/survey/error-icon.png")
												}
												width={16}
												height={16}
												alt="icon"
											/>
										</div>
										<span className="ml-3.5 flex-1 text-xs font-semibold">{subItem}</span>
									</div>
								);
							})}
						</div>

						{key === 0 && <Divider className="mt-[2.4vh]"></Divider>}
					</div>
				);
			})}
		</ContentContainer>
	);
}

export default PreUploadIDCard;
